import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Spinner } from "@/components/shared";
import { Textarea } from "@/components/ui/textarea";
import { aaLabel, AA_CATEGORIES, AA_INTERNAL_STATUSES, AA_SLA_STYLE, AA_CRISIS } from "@/lib/askAnything";
import { ArrowLeft, Send, StickyNote, ShieldAlert, User, Clock, Paperclip, Sparkles, Wand2, ListChecks, FileText, X } from "lucide-react";
import { toast } from "sonner";

const fmt = (iso) => { try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }); } catch { return ""; } };
const API_BASE = process.env.REACT_APP_BACKEND_URL;

export default function AdminAskThread() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [aiBusy, setAiBusy] = useState("");
  const [aiSummary, setAiSummary] = useState("");
  const [templates, setTemplates] = useState([]);
  const endRef = useRef(null);

  const load = async () => { setData((await api.get(`/admin/ask-anything/threads/${id}`)).data); };
  useEffect(() => { load(); api.get("/admin/ask-anything/templates").then((r) => setTemplates(r.data.templates.filter((t) => t.is_active))).catch(() => {}); }, [id]);
  useEffect(() => { endRef.current?.scrollIntoView(); }, [data?.messages?.length]);

  const runAi = async (type) => {
    setAiBusy(type);
    try {
      const { data: res } = await api.post(`/admin/ask-anything/threads/${id}/ai-${type}`);
      if (type === "summary") { setAiSummary(res.output); }
      else if (type === "draft") { setReply(res.output); toast.success("Draft inserted — review and edit before sending."); }
      else if (type === "next-steps") { setReply((r) => (r ? r + "\n\n" : "") + res.output); toast.success("Next steps added to your reply."); }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "AI tool failed."); }
    finally { setAiBusy(""); }
  };
  const insertTemplate = (tplId) => {
    const t = templates.find((x) => x.id === tplId);
    if (t) setReply((r) => (r ? r + "\n\n" : "") + t.body);
  };

  const sendReply = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try { await api.post(`/admin/ask-anything/threads/${id}/messages`, { body: reply.trim() }); setReply(""); await load(); toast.success("Reply sent to the family."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const addNote = async () => {
    if (!note.trim()) return;
    setBusy(true);
    try { await api.post(`/admin/ask-anything/threads/${id}/notes`, { note: note.trim() }); setNote(""); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const patch = async (payload) => {
    try { await api.patch(`/admin/ask-anything/threads/${id}`, payload); await load(); toast.success("Ticket updated."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return <Spinner />;
  const { thread, user, messages, notes } = data;
  const sla = AA_SLA_STYLE[thread.sla_status] || AA_SLA_STYLE.on_track;

  return (
    <div>
      <Link to="/ops/ask-anything" className="inline-flex items-center gap-1.5 text-sm text-kid-muted hover:text-kid-teal mb-4" data-testid="aa-admin-back"><ArrowLeft size={16} /> All tickets</Link>

      {thread.safety_flag && (
        <div className="rounded-xl bg-kid-coral/10 border border-kid-coral/30 p-4 mb-4 flex items-start gap-3" data-testid="aa-safety-banner">
          <ShieldAlert size={20} className="text-kid-coral shrink-0 mt-0.5" />
          <div><p className="font-bold text-kid-coral">Safety flagged</p><p className="text-sm text-kid-ink/80">{AA_CRISIS}</p></div>
        </div>
      )}

      <div className="grid lg:grid-cols-3 gap-5">
        {/* Conversation */}
        <div className="lg:col-span-2">
          <div className="kid-card p-5 mb-4">
            <h1 className="text-lg font-extrabold text-kid-navy">{thread.title}</h1>
            <div className="flex items-center gap-2 mt-2 flex-wrap">
              <span className={`text-[.65rem] font-bold uppercase px-2 py-0.5 rounded-full ${sla.cls}`}>{sla.label}</span>
              <span className="text-xs text-kid-muted">{aaLabel(AA_CATEGORIES, thread.category)}</span>
              <span className="text-xs text-kid-muted flex items-center gap-1"><Clock size={12} /> due {fmt(thread.sla_due_at)}</span>
            </div>
          </div>

          <div className="space-y-3 mb-4" data-testid="aa-admin-messages">
            {messages.map((m) => {
              const isUser = m.sender_type === "user";
              return (
                <div key={m.id} className={`flex ${isUser ? "justify-start" : "justify-end"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap ${isUser ? "bg-kid-mist border border-kid-line text-kid-ink" : "bg-kid-teal text-white"}`}>
                    <div className={`text-[.7rem] font-bold uppercase mb-1 ${isUser ? "text-kid-muted" : "text-white/80"}`}>{isUser ? user.name || "Family" : "KidPath"}</div>
                    {m.body}
                    {(m.attachments || []).map((a, i) => (
                      <a key={i} href={`${API_BASE}/api/ask-anything/threads/${id}/attachment?key=${encodeURIComponent(a.storage_key)}`} target="_blank" rel="noreferrer"
                        className={`mt-2 flex items-center gap-1.5 text-xs font-semibold underline ${isUser ? "text-kid-teal" : "text-white/90"}`}><Paperclip size={12} /> {a.file_name}</a>
                    ))}
                    <div className={`text-[.65rem] mt-1.5 ${isUser ? "text-kid-muted" : "text-white/70"}`}>{fmt(m.created_at)}</div>
                  </div>
                </div>
              );
            })}
            <div ref={endRef} />
          </div>

          {/* Reply editor */}
          <div className="kid-card p-4" data-testid="aa-admin-reply">
            {aiSummary && (
              <div className="bg-kid-skytint rounded-xl p-3 mb-3 relative" data-testid="aa-ai-summary">
                <button onClick={() => setAiSummary("")} className="absolute top-2 right-2 text-kid-muted hover:text-kid-navy"><X size={14} /></button>
                <div className="text-xs font-bold uppercase tracking-wide text-kid-teal mb-1 flex items-center gap-1"><Sparkles size={12} /> AI summary (internal)</div>
                <p className="text-sm text-kid-ink/85 whitespace-pre-wrap">{aiSummary}</p>
              </div>
            )}
            <div className="flex items-center gap-2 mb-2 flex-wrap" data-testid="aa-ai-tools">
              <button onClick={() => runAi("summary")} disabled={!!aiBusy} className="inline-flex items-center gap-1 text-xs font-poppins font-semibold text-kid-teal bg-kid-skytint hover:bg-kid-teal hover:text-white px-3 py-1.5 rounded-full transition-colors disabled:opacity-60" data-testid="aa-ai-summarize">
                <Sparkles size={13} /> {aiBusy === "summary" ? "…" : "Summarize"}
              </button>
              <button onClick={() => runAi("draft")} disabled={!!aiBusy} className="inline-flex items-center gap-1 text-xs font-poppins font-semibold text-kid-teal bg-kid-skytint hover:bg-kid-teal hover:text-white px-3 py-1.5 rounded-full transition-colors disabled:opacity-60" data-testid="aa-ai-draft">
                <Wand2 size={13} /> {aiBusy === "draft" ? "Drafting…" : "Suggest draft"}
              </button>
              <button onClick={() => runAi("next-steps")} disabled={!!aiBusy} className="inline-flex items-center gap-1 text-xs font-poppins font-semibold text-kid-teal bg-kid-skytint hover:bg-kid-teal hover:text-white px-3 py-1.5 rounded-full transition-colors disabled:opacity-60" data-testid="aa-ai-nextsteps">
                <ListChecks size={13} /> {aiBusy === "next-steps" ? "…" : "Next steps"}
              </button>
              {templates.length > 0 && (
                <select onChange={(e) => { if (e.target.value) { insertTemplate(e.target.value); e.target.value = ""; } }} defaultValue="" data-testid="aa-template-select"
                  className="text-xs font-poppins font-semibold text-kid-navy bg-white border border-kid-line rounded-full px-3 py-1.5">
                  <option value="">Insert template…</option>
                  {templates.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
                </select>
              )}
            </div>
            <Textarea value={reply} onChange={(e) => setReply(e.target.value.slice(0, 5000))} rows={6} placeholder="Write a warm, clear reply. Acknowledge → normalize → 2–4 next steps → boundary/disclaimer → invite to save." className="rounded-xl resize-none" data-testid="aa-admin-reply-input" />
            <p className="text-xs text-kid-muted mt-1.5">AI suggestions are drafts for you to review and edit — nothing is sent to the family automatically.</p>
            <div className="flex justify-end mt-2">
              <button onClick={sendReply} disabled={busy || !reply.trim()} className="kid-btn kid-btn-primary h-10 px-5 disabled:opacity-50" data-testid="aa-admin-reply-send"><Send size={15} /> Send reply</button>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <div className="kid-card p-4" data-testid="aa-admin-user">
            <div className="flex items-center gap-2 text-kid-navy font-bold mb-2"><User size={16} /> Family</div>
            <dl className="text-sm space-y-1">
              <div className="flex justify-between"><dt className="text-kid-muted">Name</dt><dd className="text-kid-navy font-medium">{user.name || "—"}</dd></div>
              <div className="flex justify-between"><dt className="text-kid-muted">Email</dt><dd className="text-kid-navy font-medium truncate ml-2">{user.email}</dd></div>
              <div className="flex justify-between"><dt className="text-kid-muted">Plan</dt><dd className="text-kid-navy font-medium">{user.plan_key} · {user.subscription_status}</dd></div>
              <div className="flex justify-between"><dt className="text-kid-muted">Province</dt><dd className="text-kid-navy font-medium">{user.province || "—"}</dd></div>
            </dl>
          </div>

          <div className="kid-card p-4 space-y-3" data-testid="aa-admin-controls">
            <div className="flex items-center gap-2 text-kid-navy font-bold"><StickyNote size={16} /> Ticket controls</div>
            <div className="space-y-1.5">
              <label className="text-xs text-kid-muted">Status</label>
              <select value={thread.status} onChange={(e) => patch({ status: e.target.value })} className="h-10 w-full rounded-xl border border-kid-line px-2 bg-white text-sm" data-testid="aa-admin-status">
                {AA_INTERNAL_STATUSES.map((s) => <option key={s.value} value={s.value}>{s.label}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-kid-muted">Priority</label>
              <select value={thread.priority} onChange={(e) => patch({ priority: e.target.value })} className="h-10 w-full rounded-xl border border-kid-line px-2 bg-white text-sm" data-testid="aa-admin-priority">
                <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-xs text-kid-muted">Category</label>
              <select value={thread.category} onChange={(e) => patch({ category: e.target.value })} className="h-10 w-full rounded-xl border border-kid-line px-2 bg-white text-sm" data-testid="aa-admin-category">
                {AA_CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div className="flex gap-2 pt-1">
              {!thread.assigned_admin_id && <button onClick={() => patch({ assign_to_me: true })} className="kid-btn kid-btn-ghost h-9 px-3 text-xs flex-1" data-testid="aa-admin-assign">Assign to me</button>}
              <button onClick={() => patch({ safety_flag: !thread.safety_flag, safety_flag_type: thread.safety_flag ? null : "manual" })}
                className={`kid-btn h-9 px-3 text-xs flex-1 ${thread.safety_flag ? "kid-btn-ghost text-kid-muted" : "kid-btn-ghost text-kid-coral"}`} data-testid="aa-admin-safety-toggle">
                <ShieldAlert size={13} /> {thread.safety_flag ? "Unflag" : "Flag safety"}
              </button>
            </div>
          </div>

          <div className="kid-card p-4" data-testid="aa-admin-disclaimer">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-kid-navy font-bold text-sm">Suggested disclaimer</div>
              <button onClick={() => { navigator.clipboard?.writeText(thread.disclaimer_block || ""); toast.success("Disclaimer copied."); }}
                className="text-xs text-kid-teal font-semibold hover:underline" data-testid="aa-copy-disclaimer">Copy</button>
            </div>
            <p className="text-xs text-kid-muted leading-relaxed">{thread.disclaimer_block}</p>
          </div>

          <div className="kid-card p-4" data-testid="aa-admin-notes">
            <div className="flex items-center gap-2 text-kid-navy font-bold mb-2"><StickyNote size={16} /> Internal notes</div>
            <div className="space-y-2 mb-3 max-h-48 overflow-y-auto">
              {notes.length === 0 ? <p className="text-xs text-kid-muted">No internal notes yet.</p> :
                notes.map((n) => <div key={n.id} className="bg-kid-sun/10 rounded-lg p-2 text-xs text-kid-ink" data-testid="aa-note-item"><p>{n.note}</p><p className="text-kid-muted mt-1">{fmt(n.created_at)}</p></div>)}
            </div>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="Add an internal note (not visible to the family)…" className="rounded-xl resize-none text-sm" data-testid="aa-note-input" />
            <button onClick={addNote} disabled={busy || !note.trim()} className="kid-btn kid-btn-ghost h-9 px-4 text-sm mt-2 w-full disabled:opacity-50" data-testid="aa-note-add">Add note</button>
          </div>
        </div>
      </div>
    </div>
  );
}
